import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ConfirmCleanDialogComponent } from "../confirm-clean-dialog/confirm-clean-dialog.component";
import { AuditQuestion, AuditResponse, AuditRating, RATING_POINTS } from "../../models/lab-audit.models";

export interface AuditResponseChange {
  questionId: string;
  changes: Partial<AuditResponse>;
}

@Component({
  selector: "app-audit-questionnaire",
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmCleanDialogComponent],
  templateUrl: "./audit-questionnaire.component.html",
  styleUrl: "./audit-questionnaire.component.css",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditQuestionnaireComponent {
  @Input({ required: true }) title = "";
  @Input({ required: true }) questions: AuditQuestion[] = [];
  @Input({ required: true }) responses: Record<string, AuditResponse> = {};
  @Input() score = 0;
  @Input() maximumScore = 80;
  @Input() additionalComments = "";
  @Input() showCleanButton = true;
  @Output() responseChange = new EventEmitter<AuditResponseChange>();
  @Output() additionalCommentsChange = new EventEmitter<string>();
  @Output() cleanConfirmed = new EventEmitter<void>();
  @ViewChild(ConfirmCleanDialogComponent) private cleanDialog?: ConfirmCleanDialogComponent;

  readonly ratings: { value: Exclude<AuditRating, "">; label: string; points: number }[] = [
    { value: "F", label: "Fails", points: 0 },
    { value: "N", label: "Needs attention", points: 2 },
    { value: "G", label: "Good", points: 4 },
    { value: "E", label: "Exceeds", points: 5 },
  ];
  get answeredCount(): number {
    return this.questions.filter(question => !!this.responses[question.id]?.rating).length;
  }

  get compatibility(): number {
    return this.maximumScore ? this.score / this.maximumScore : 0;
  }

  responseFor(questionId: string): AuditResponse {
    return this.responses[questionId] ?? { answer: "", rating: "", comments: "" };
  }

  questionNumber(question: AuditQuestion): number {
    return this.questions.findIndex(item => item.id === question.id) + 1;
  }

  showGroupHeading(index: number): boolean {
    return !!this.questions[index].group && this.questions[index - 1]?.group !== this.questions[index].group;
  }

  selectAnswer(questionId: string, answer: "yes" | "no"): void {
    const current = this.responseFor(questionId);
    this.responseChange.emit({ questionId, changes: { answer: current.answer === answer ? "" : answer } });
  }

  selectRating(questionId: string, rating: Exclude<AuditRating, "">): void {
    const current = this.responseFor(questionId);
    this.responseChange.emit({ questionId, changes: { rating: current.rating === rating ? "" : rating } });
  }

  updateComments(questionId: string, comments: string): void {
    this.responseChange.emit({
      questionId,
      changes: { comments },
    });
  }

  updateAdditionalComments(comments: string): void {
    this.additionalCommentsChange.emit(comments);
  }

  pointsFor(questionId: string): number | null {
    const rating = this.responseFor(questionId).rating;
    return rating ? RATING_POINTS[rating] : null;
  }

  openCleanDialog(): void {
    this.cleanDialog?.open();
  }
}
